// Mirrors CSC_CORE/Features/QuotationIntake/Objects/DTOs/FreightLocationDto.cs
// Sourced from NetSuite customrecord_truckingrates via the singleton FreightLocationCache.
// `dopRate` maps to the "DOP Rates" column (custrecord_dop_truckingrates) — the single
// per-ton rate the freight calculator bills against.
export interface FreightLocationDto {
  id: string;
  name: string;
  cityName: string;
  dopRate: number;
}

// User-chosen truck class. Mirrors CSC_CORE/Features/QuotationIntake/Services/FreightAllocator.cs
// constants — keep in sync.
export type TruckType = 'trailer' | '10wh';

export const TRUCK_TYPES: TruckType[] = ['trailer', '10wh'];

export interface TruckSpec {
  /** Max load a single truck can carry, in tons. */
  maxLoad: number;
  /**
   * Fixed minimum billable load per truck, in tons. 10-wheelers: 20. For a trailer this is only the
   * fallback for a quotation with no saved choice (40) — read it through `resolveMinBillable`.
   */
  minBillable: number;
  /** Human label for UI rendering. */
  label: string;
}

/**
 * 14ykpqtvw4a — the trailer minimums sales may choose per quotation. Mirrors
 * FreightAllocator.TrailerMinBillOptionsTons — keep in sync.
 */
export const TRAILER_MIN_BILL_OPTIONS = [40, 45, 50] as const;
export type TrailerMinBill = (typeof TRAILER_MIN_BILL_OPTIONS)[number];

/** Where a quotation priced from scratch starts. Mirrors FreightAllocator.DefaultTrailerMinBillTons. */
export const DEFAULT_TRAILER_MIN_BILL: TrailerMinBill = 45;

/**
 * What a MISSING choice means: the fixed minimum every trailer was billed at before the choice existed.
 * Owner ruling 2026-10-19 — quotations priced before 14ykpqtvw4a keep it until sales picks another value.
 * Mirrors FreightAllocator.LegacyTrailerMinBillTons.
 */
export const LEGACY_TRAILER_MIN_BILL: TrailerMinBill = 40;

/**
 * 14ykpqtvw4a (owner, 2026-10-19) — freight weighs in tons to 4 decimal places (0.1 kg), rounded ONCE so the
 * truck count, padding, money and every displayed figure share one number and the breakdown reproduces on a
 * calculator. Mirrors FreightAllocator.RoundFreightTons (decimal, MidpointRounding.AwayFromZero). The tiny
 * nudge keeps a decimal midpoint that binary floating point stored a hair low (10.86545 → 108654.4999…)
 * rounding UP as the server does; a weight off a midpoint is never within 1e-6 of one, because line weights
 * carry at most 4 decimals in kg.
 */
export const FREIGHT_WEIGHT_DECIMALS = 4;

export function roundFreightTons(tons: number): number {
  const scale = 10 ** FREIGHT_WEIGHT_DECIMALS;
  return Math.round(tons * scale + 1e-6) / scale;
}

/**
 * A freight line rounded to the centavo, half away from zero — mirrors FreightAllocator.RoundPeso. Lines are
 * rounded BEFORE summing so the rows the cabinet prints add up to its total, and that total is the server's
 * ComputedAmount (owner: "the screen and the computation always match, and what gets sent"). Same midpoint
 * nudge as `roundFreightTons`: weight (4 dp) × rate (2 dp) has at most 6 dp, so only a true midpoint is
 * within 1e-6 of one.
 */
export function roundPeso(amount: number): number {
  return (Math.sign(amount) * Math.round(Math.abs(amount) * 100 + 1e-6)) / 100;
}

export function isTrailerMinBill(value: unknown): value is TrailerMinBill {
  return (TRAILER_MIN_BILL_OPTIONS as readonly unknown[]).includes(value);
}

/**
 * The trailer minimum the cabinet shows and bills for one quotation. A saved choice always wins. With none
 * saved, a quote that already has a destination was priced under the old fixed 40 and keeps it until sales
 * picks another value (owner ruling 2026-10-19); one with no destination yet is being priced from scratch
 * and starts at 45. The cabinet pins this value onto the draft when a destination is picked, so the server
 * — which reads a missing value as 40 — bills exactly what was shown.
 */
export function effectiveTrailerMinBill(saved: TrailerMinBill | null, hasDestination: boolean): TrailerMinBill {
  return saved ?? (hasDestination ? LEGACY_TRAILER_MIN_BILL : DEFAULT_TRAILER_MIN_BILL);
}

export const TRUCK_SPECS: Record<TruckType, TruckSpec> = {
  trailer: { maxLoad: 50, minBillable: LEGACY_TRAILER_MIN_BILL, label: 'Trailer' },
  '10wh':  { maxLoad: 20, minBillable: 20, label: '10-wheeler' },
};

/**
 * The minimum billable tons per truck for one quotation: the saved choice for a trailer, the fixed floor
 * otherwise. A missing trailer choice resolves to the legacy 40. Mirrors FreightAllocator.ResolveMinBillable.
 */
export function resolveMinBillable(truckType: string, trailerMinBillTons: number | null | undefined): number {
  const spec = TRUCK_SPECS[truckType as TruckType];
  if (!spec) return 0;
  return truckType === 'trailer' && trailerMinBillTons != null ? trailerMinBillTons : spec.minBillable;
}

/**
 * 14ykpqtw3ua — the max load per truck is empty (no cap) or a WHOLE number of tons ≥ 1. With a decimal cap this
 * side's float `Math.ceil` and the server's decimal `Math.Ceiling` can disagree on the truck count (99.9 t at a
 * 33.3 t cap shows 4 trucks and bills 3). Mirrors FreightAllocator.IsAllowedMaxLoadCap — keep in sync.
 */
export function isAllowedMaxLoadCap(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isInteger(value) && value >= 1);
}

/**
 * The load one truck carries for sizing the fleet: the cap when it is below the truck's own limit, the truck's
 * limit otherwise (a cap at or above it has no effect). Mirrors FreightAllocator.ResolveMaxLoad.
 */
export function resolveMaxLoad(spec: TruckSpec, cap: number | null | undefined): number {
  return cap != null && cap < spec.maxLoad ? cap : spec.maxLoad;
}

/**
 * The cap an allocation records as APPLIED: the resolved max load only when it CHANGED THE TRUCK COUNT
 * (`ceil(weight / resolveMaxLoad(spec, cap)) > ceil(weight / spec.maxLoad)`), null otherwise. Owner decision
 * 2026-10-20 (14ykpqtw3ua): the "(capped)" caption, the "(max 45 t each)" toll row and the read-only summary row
 * appear only when the cap added trucks. Trucks and money always come from `resolveMaxLoad`. `totalWeightTons`
 * is the already-rounded freight weight. Mirrors FreightAllocator.AppliedMaxLoadCap — keep in sync.
 */
export function appliedMaxLoadCap(spec: TruckSpec, cap: number | null | undefined, totalWeightTons: number): number | null {
  const maxLoad = resolveMaxLoad(spec, cap);
  if (maxLoad >= spec.maxLoad || maxLoad <= 0) return null;
  return Math.ceil(totalWeightTons / maxLoad) > Math.ceil(totalWeightTons / spec.maxLoad) ? maxLoad : null;
}

/** Metro Manila per-truck flat rate for 10-wheelers (PHP). Mirrors FreightAllocator.MmTenWheelerFlatPhp. */
export const MM_TENWH_FLAT_PHP = 7_500;
/** Metro Manila per-truck flat rate for trailers (PHP). Mirrors FreightAllocator.MmTrailerFlatPhp. */
export const MM_TRAILER_FLAT_PHP = 17_600;

/** Server BillingMode discriminator on FreightSnapshot. */
export type BillingMode = 'PER_TON' | 'MM_FLAT';

export interface AllocationInputs {
  /** Total weight to ship, in metric tons. The allocator rounds it to 4 dp (`roundFreightTons`) on the way in. */
  totalWeightTons: number;
  /**
   * Sales-toggled MM flag. When true, the allocator uses the flat-rate path and ignores
   * `truckType` (auto-derives from `hasTwelveMeterLengths` + `totalWeightTons`),
   * `rate`, `discount`, `tollPerTruck`, and `locationMaxLoadPerTruck`.
   */
  withinMetroManila: boolean;
  /**
   * True if any line item has length ≥ 12m. Forces a trailer in MM mode (10-wheelers
   * can't carry 12m). Ignored on the per-ton path.
   */
  hasTwelveMeterLengths: boolean;
  /** Truck class — selects which TruckSpec governs billing on the per-ton path. Ignored in MM mode. */
  truckType: TruckType;
  /**
   * The quotation's trailer minimum (40 / 45 / 50). Null/absent = never chosen = the legacy 40.
   * Ignored for 10-wheelers and in MM mode.
   */
  trailerMinBillTons?: number | null;
  /** Per-ton rate at the destination, in PHP. Ignored in MM mode. */
  rate: number;
  /** Discount per ton, in PHP. Defaults to 110 in the cabinet UI; editable. Ignored in MM mode. */
  discount: number;
  /** Toll fee per truck per trip, in PHP. Ignored in MM mode. */
  tollPerTruck: number;
  /**
   * Optional cap on physical load per truck, in whole tons (`isAllowedMaxLoadCap`). When set, each
   * truck's effective max load is `resolveMaxLoad(spec, locationMaxLoadPerTruck)` —
   * useful when a destination cannot accept a fully loaded truck for road/access
   * reasons. Billing still floors at the per-truck minimum (`resolveMinBillable`). 14ykpqtw3ua — persisted
   * on the input draft as `freightMaxLoadPerTruck` and billed by the server the same way. Ignored in MM mode.
   */
  locationMaxLoadPerTruck?: number;
}

export interface Allocation {
  /** Truck type the allocation was sized for. Server-derived when withinMetroManila is true. */
  truckType: TruckType;
  /** Number of trucks needed. */
  numTrucks: number;
  /** Per-truck floor this allocation billed at (PER_TON). 0 on MM rows — flat rate has no floor. */
  minBillPerTruck: number;
  /**
   * 14ykpqtw3ua — the max load per truck this allocation was sized at, recorded only when the cap changed the
   * truck count (`appliedMaxLoadCap`); null otherwise, and always on MM rows. Mirrors PerTonDopRate.MaxLoadPerTruck.
   */
  maxLoadPerTruck: number | null;
  /** max(delivered, numTrucks × minBillable) for PER_TON; equals delivered for MM_FLAT (no padding concept). */
  bookedCapacity: number;
  /** Delivered weight, equals input weight. */
  delivered: number;
  /** bookedCapacity − delivered. 0 on MM rows. */
  padding: number;
  /** Which billing rule produced this allocation. */
  billingMode: BillingMode;
  /** Per-truck flat rate (PHP) on MM rows. Null for PER_TON. */
  flatRatePerTruck: number | null;
}

export interface CostBreakdown {
  /** delivered × (rate − discount). 0 on MM rows. */
  deliveredCost: number;
  /** padding × rate (no discount on unused capacity). 0 on MM rows. */
  paddingCost: number;
  /** numTrucks × tollPerTruck. 0 on MM rows. */
  tollCost: number;
  /** numTrucks × flatRatePerTruck. 0 on PER_TON rows. */
  mmFlatCost: number;
  /** Raw formula result. Kept on the breakdown so chart/audit consumers can see
   *  the unrounded number; the cabinet displays it labelled "Computed". */
  total: number;
  /** `total` rounded up to the nearest ₱1,000 — matches the server's BilledAmount and
   *  what hits NetSuite grossAmt. Customer-facing value for any "what will we charge?"
   *  display. */
  billedTotal: number;
  /** total / delivered. NaN when delivered === 0. */
  effectiveRatePerTon: number;
}

export interface AllocationOption extends Allocation {
  cost: CostBreakdown;
}
